import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { api } from "../../data";
import { Dialog, useAction } from "../../components/ui";
import { date } from "../../lib/format";
import { Fehler, Laden } from "./parts";
import { OwnerKuerzel, StufePill, staffName, useIch, useLeads, useStaff, ZustaendigFilter } from "./crm-teile";
import {
  ALLE_LEADS, createLead, faelligkeit, filterLeads, heuteBerlin, leadName, QUELLEN, quelleLabel, STUFEN, type LeadFilter, type LeadInput, type Quelle,
} from "./crm-data";

/**
 * Leadliste (Vertrieb-Konsole, Inhaber 02.11.2026): alle Leads mit Filtern nach Zuständig, Stufe, Quelle und Fälligkeit; neue
 * Leads von Hand (Antwortende aus Instagram, Mail, Empfehlung). Akquise-Listen werden bewusst nicht importiert.
 */
export function Leads() {
  const q = useLeads();
  const staff = useStaff();
  const ich = useIch();
  const [filter, setFilter] = useState<LeadFilter>(ALLE_LEADS);
  const heute = heuteBerlin();
  const aktive = useMemo(() => new Set((staff.data ?? []).filter((s) => s.active).map((s) => s.user_id)), [staff.data]);
  const zeilen = useMemo(() => filterLeads(q.data ?? [], filter, ich, heute, aktive), [q.data, filter, ich, heute, aktive]);
  const gefiltert = JSON.stringify(filter) !== JSON.stringify(ALLE_LEADS);
  const kopf = <><title>Leads · Konsole · CreatorOffice</title>
    <div className="pagehead"><div><h1>Leads</h1><p className="muted">Echte Interessenten: Demo-Anfragen, Testkonten, Enterprise-Anfragen und Antwortende.</p></div><NeuerLead /></div></>;
  if (q.error) return <>{kopf}<Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending) return <>{kopf}<Laden /></>;
  return (
    <>
      {kopf}
      <div className="toolbar tk-crm-filter">
        <input type="search" aria-label="Leads durchsuchen" placeholder="Firma, Kontakt, E-Mail, Thema …" value={filter.suche} onChange={(e) => setFilter({ ...filter, suche: e.target.value })} />
        <ZustaendigFilter value={filter.zustaendig} onChange={(zustaendig) => setFilter({ ...filter, zustaendig })} />
        <select aria-label="Stufe" value={filter.stufe} onChange={(e) => setFilter({ ...filter, stufe: e.target.value as LeadFilter["stufe"] })}>
          <option value="offen">Offene Stufen</option>
          <option value="alle">Alle Stufen</option>
          {STUFEN.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select aria-label="Quelle" value={filter.quelle} onChange={(e) => setFilter({ ...filter, quelle: e.target.value as LeadFilter["quelle"] })}>
          <option value="alle">Alle Quellen</option>
          {QUELLEN.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
        <select aria-label="Fällig" value={filter.faellig} onChange={(e) => setFilter({ ...filter, faellig: e.target.value as LeadFilter["faellig"] })}>
          <option value="alle">Fällig: egal</option>
          <option value="faellig">Heute oder überfällig</option>
          <option value="ohne_schritt">Ohne nächsten Schritt</option>
        </select>
        {gefiltert && <button type="button" onClick={() => setFilter(ALLE_LEADS)}>Filter zurücksetzen</button>}
      </div>
      <div className="tablewrap">
        <table className="tk-crm-tabelle">
          <caption className="sr-only">Leads</caption>
          <thead><tr><th scope="col">Lead</th><th scope="col">Stufe</th><th scope="col">Zuständig</th><th scope="col">Quelle</th><th scope="col">Nächster Schritt</th><th scope="col">Letzter Kontakt</th></tr></thead>
          <tbody>
            {zeilen.map((l) => {
              const f = faelligkeit(l.next_step_at, heute);
              return (
                <tr key={l.id}>
                  <td><Link to={`/intern/leads/${l.id}`}><strong>{leadName(l)}</strong></Link>
                    <small>{[l.company ? l.contact_name : null, l.email].filter(Boolean).join(" · ") || "–"}</small>
                    {l.org_id && <small><Link to={`/intern/agenturen/${l.org_id}`}>Kundenakte</Link></small>}</td>
                  <td><StufePill stufe={l.stage} />{l.stage === "verloren" && l.lost_reason && <small>{l.lost_reason}</small>}</td>
                  <td><span className="row tk-crm-owner-zeile"><OwnerKuerzel staff={staff.data} id={l.owner_id} /><small>{staffName(staff.data, l.owner_id)}</small></span></td>
                  <td>{quelleLabel(l.source)}{l.source_detail && <small>{l.source_detail}</small>}</td>
                  <td>{l.next_step_at ? <span className={f === "ueberfaellig" ? "tk-crm-ueberfaellig" : f === "heute" ? "tk-crm-heute" : undefined}>{date(l.next_step_at)}</span> : "–"}
                    {l.next_step && <small>{l.next_step}</small>}</td>
                  <td>{l.last_contact_at ? date(l.last_contact_at) : "–"}</td>
                </tr>
              );
            })}
            {zeilen.length === 0 && <tr><td colSpan={6}>{(q.data ?? []).length ? "Keine passenden Leads. Passe die Suche oder die Filter an." : "Noch keine Leads. Neue Demo-Anfragen, Testkonten und Enterprise-Anfragen erscheinen hier von selbst."}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="source-note">{zeilen.length === 1 ? "1 Lead" : `${zeilen.length} Leads`} · Leads ohne Konto und ohne Kontakt seit 24 Monaten werden automatisch gelöscht.</p>
    </>
  );
}

/** Neuer Lead von Hand (Antwortende); zuständig ist, wer ihn anlegt */
export function NeuerLead() {
  const [offen, setOffen] = useState(false);
  const { busy, error, run } = useAction();
  const navigate = useNavigate();
  const leer: LeadInput = { company: "", contact_name: "", email: "", phone: "", website: "", source: "instagram", source_detail: "", creators: "", country: "" };
  const [e, setE] = useState<LeadInput>(leer);
  const feld = (k: keyof LeadInput) => ({ value: (e[k] as string) ?? "", onChange: (x: { target: { value: string } }) => setE({ ...e, [k]: x.target.value }) });
  async function absenden(ev: FormEvent) {
    ev.preventDefault();
    const id = await run("neu", () => createLead(api, e), "Lead angelegt.");
    if (id) { setOffen(false); setE(leer); navigate(`/intern/leads/${id}`); }
  }
  return (
    <>
      <button type="button" className="primary" onClick={() => setOffen(true)}>Neuer Lead</button>
      <Dialog open={offen} onClose={() => setOffen(false)} title="Neuer Lead" wide>
        <form className="stack" onSubmit={(ev) => void absenden(ev)}>
          <p className="muted">Nur echte Interessenten, die geantwortet oder angefragt haben; keine Akquise-Listen.</p>
          <div className="grid tk-crm-formular">
            <label>Firma<input maxLength={160} {...feld("company")} /></label>
            <label>Kontaktperson<input maxLength={120} {...feld("contact_name")} /></label>
            <label>E-Mail<input type="email" maxLength={200} {...feld("email")} /></label>
            <label>Telefon<input type="tel" maxLength={40} {...feld("phone")} /></label>
            <label>Website oder Instagram<input maxLength={200} {...feld("website")} /></label>
            <label>Land (z. B. DE)<input maxLength={2} {...feld("country")} /></label>
            <label>Anzahl Creator<input maxLength={30} {...feld("creators")} /></label>
            <label>Quelle
              <select value={e.source} onChange={(x) => setE({ ...e, source: x.target.value as Quelle })}>
                {QUELLEN.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </label>
            <label className="tk-crm-breit">Detail zur Quelle (Kampagne, Ref, Gespräch)<input maxLength={300} {...feld("source_detail")} /></label>
          </div>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button type="submit" className="primary" disabled={!!busy} aria-busy={busy === "neu"}>{busy === "neu" ? "Wird angelegt …" : "Lead anlegen"}</button>
            <button type="button" onClick={() => setOffen(false)}>Abbrechen</button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
